import type { AdType } from '../../types/campaign';

// Ad Preview Mockups Stage 1 §2.1. Small dropdown next to a Drive asset's
// label that lets the AM classify the asset as Story / Feed / —.
//
// Used inside DriveAssetCard's edit mode. Auto-population happens upstream
// (DriveAssetGrid.upsertLabel runs detectAdTypeFromFilename when a label
// is created); this dropdown is the override + display surface.
//
// View-only display lives in <AdTypeBadge /> below — separated so callers
// can render a badge on its own (e.g. Build View's read-only asset cards)
// without dragging in the editable form control.

type Props = {
  value: AdType | undefined;
  onChange: (next: AdType) => void;
  // Optional accessible label override. When the surrounding card already
  // labels the dropdown (e.g. a <label> wrapping the select), pass the same
  // text here so screen readers don't read it twice.
  ariaLabel?: string;
};

export function AdTypeDropdown({ value, onChange, ariaLabel }: Props) {
  // `value` may be undefined on legacy labels — coerce to '' so the
  // controlled select lands on the "—" option rather than going
  // uncontrolled.
  const select = value ?? '';
  return (
    <select
      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
      value={select}
      aria-label={ariaLabel ?? 'Ad type'}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? null : (v as 'story' | 'feed'));
      }}
    >
      <option value="">— (uncategorized)</option>
      <option value="story">Story</option>
      <option value="feed">Feed</option>
    </select>
  );
}

// Read-only badge — shown in view-mode cards (Build View, Row Editor) and
// inside the public preview page where editing is meaningless.
//
// Renders nothing when adType is null/undefined so the card layout doesn't
// reserve dead vertical space for uncategorized assets.
export function AdTypeBadge({ adType }: { adType: AdType | undefined }) {
  if (!adType) return null;
  const label = adType === 'story' ? 'Story' : 'Feed';
  const icon = adType === 'story' ? '🎬' : '🟦';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
      title={`Ad type: ${label}`}
    >
      <span aria-hidden>{icon}</span>
      <span>{label}</span>
    </span>
  );
}
